import { useCallback, useState } from 'react';

import { buildParticipation } from '@api/builders';
import { createScreenerResponse, simulateScreenerResponse } from '@api/public_endpoints';

interface UseParticipationDataProps {
  screener: Screener;
  study: PublicStudy;
  participation?: Participation | null;
  isPreview?: boolean;
}

interface UseParticipationDataReturn {
  participation: Participation | null;
  submitAnswers: (answers: any) => Promise<Participation | null>;
  submitting: boolean;
  error: any;
}

export const useParticipationData = ({
  screener,
  study,
  participation: initialParticipation,
  isPreview = false
}: UseParticipationDataProps): UseParticipationDataReturn => {
  const [participation, setParticipation] = useState<Participation | null>(initialParticipation || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<any>(null);

  const submitAnswers = useCallback(
    async (answers: any) => {
      setSubmitting(true);
      setError(null);

      try {
        if (isPreview) {
          // In preview mode, simulate the participation
          const simulatedParticipation = await simulateScreenerResponse(
            screener.id,
            answers,
            study.workspace,
            study.slug
          );

          // Transform simulated participation to match the real structure
          const processedParticipation = {
            ...simulatedParticipation,
            // Ensure we have the structure expected by ConfirmationPage
            activities: simulatedParticipation.activities || []
          } as Participation;

          setParticipation(processedParticipation);
          return processedParticipation;
        } else {
          // In real mode, create actual screener response
          const resp = await createScreenerResponse(
            screener.id,
            participation?.id,
            answers,
            study.workspace,
            study.slug
          );

          const { participation: part } = resp;
          const processedParticipation = buildParticipation({ attributes: part });
          setParticipation(processedParticipation);
          return processedParticipation;
        }
      } catch (err) {
        setError(err);
        throw err; // Re-throw to allow component to handle specific errors
      } finally {
        setSubmitting(false);
      }
    },
    [screener.id, study.workspace, study.slug, participation?.id, isPreview]
  );

  return {
    participation,
    submitAnswers,
    submitting,
    error
  };
};
